import { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { buildBookSpreads, type AlbumDocument, type AlbumPage } from "../domain/project";
import { PhotoFrame } from "./PhotoFrame";

interface BookAlbumPreviewProps {
  document: AlbumDocument;
  urls: Record<string, string>;
}

type Direction = "next" | "prev";

const SWIPE_DISTANCE = 44;

function BookPage({ page, pageNumber, url, side }: {
  page?: AlbumPage;
  pageNumber?: number;
  url?: string;
  side: "left" | "right";
}) {
  if (!page) {
    return <div className={`book-page book-page-blank book-page-${side}`} aria-label="空白页"><span>PHOTO FOLIO</span></div>;
  }
  return (
    <article className={`book-page book-page-${side}`}>
      <div className="book-page-number">{String(pageNumber).padStart(2, "0")}</div>
      <PhotoFrame src={url} alt={page.title || "影集照片"} presentation={page.presentation} className="book-page-media" loading="lazy" decoding="async" />
      <div className="book-page-copy">
        <div>
          <small>{[page.date, page.location].filter(Boolean).join(" / ")}</small>
          <h3>{page.title || "未命名照片"}</h3>
        </div>
        <p>{page.caption || "这张照片还没有说明。"}</p>
      </div>
    </article>
  );
}

export function BookAlbumPreview({ document, urls }: BookAlbumPreviewProps) {
  const spreads = useMemo(() => buildBookSpreads(document.pages), [document.pages]);
  const pages = useMemo(() => spreads.flat(), [spreads]);
  const [currentPage, setCurrentPage] = useState(-1);
  const [direction, setDirection] = useState<Direction>("next");
  const [isMobile, setIsMobile] = useState(false);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    setCurrentPage((current) => {
      if (current < 0 || !pages.length) return current < 0 ? -1 : 0;
      const pageStart = isMobile ? current : current - (current % 2);
      return Math.min(pageStart, pages.length - 1);
    });
  }, [isMobile, pages.length]);

  const pageStart = currentPage < 0 || isMobile ? currentPage : currentPage - (currentPage % 2);
  const visiblePages = pageStart < 0 ? [] : isMobile ? [pages[pageStart]] : [pages[pageStart], pages[pageStart + 1]];
  const canGoBack = currentPage >= 0;
  const canGoForward = pages.length > 0 && (currentPage < 0 || pageStart + (isMobile ? 1 : 2) < pages.length);

  const move = (nextDirection: Direction) => {
    if (nextDirection === "next" && !canGoForward) return;
    if (nextDirection === "prev" && !canGoBack) return;
    setDirection(nextDirection);
    setCurrentPage((current) => {
      if (nextDirection === "next") {
        return current < 0 ? 0 : Math.min(pages.length - 1, (isMobile ? current : current - (current % 2)) + (isMobile ? 1 : 2));
      }
      if (current < 0) return -1;
      const start = isMobile ? current : current - (current % 2);
      return start <= 0 ? -1 : start - (isMobile ? 1 : 2);
    });
  };

  const progress = currentPage < 0
    ? "封面"
    : isMobile
      ? `${pageStart + 1} / ${pages.length}`
      : `${pageStart + 1}–${Math.min(pageStart + 2, pages.length)} / ${pages.length}`;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      move("prev");
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      move("next");
    }
  };

  return (
    <section
      className="book-reader"
      role="region"
      aria-label="翻页影集阅读器"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onPointerDown={(event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        pointerStart.current = { x: event.clientX, y: event.clientY };
      }}
      onPointerUp={(event) => {
        const start = pointerStart.current;
        pointerStart.current = null;
        if (!start) return;
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        if (Math.abs(deltaX) < SWIPE_DISTANCE || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        move(deltaX < 0 ? "next" : "prev");
      }}
      onPointerCancel={() => { pointerStart.current = null; }}
    >
      <div className="book-stage">
        {currentPage < 0 ? (
          <article className="book-cover">
            <div className="book-cover-copy">
              <span className="book-cover-mark"><BookOpen size={15} weight="fill" /> PHOTO FOLIO</span>
              <h2>{document.title || "未命名影集"}</h2>
              <p>{document.subtitle || "把经过的光，留在这里。"}</p>
              <div className="book-cover-meta">
                {[document.date, document.location].filter(Boolean).map((value) => <span key={value}>{value}</span>)}
              </div>
            </div>
            <PhotoFrame src={document.coverAssetId ? urls[document.coverAssetId] : undefined} alt={`${document.title}封面`} presentation={document.pages.find((page) => page.assetId === document.coverAssetId)?.presentation} className="book-cover-media" decoding="async" placeholder={<div className="book-cover-placeholder">等待第一张照片</div>} />
          </article>
        ) : (
          <div className={`book-spread is-${direction}`} key={`${pageStart}-${isMobile ? "single" : "double"}`}>
            <BookPage page={visiblePages[0]} pageNumber={pageStart + 1} url={visiblePages[0] ? urls[visiblePages[0].assetId] : undefined} side="left" />
            {!isMobile ? <BookPage page={visiblePages[1]} pageNumber={pageStart + 2} url={visiblePages[1] ? urls[visiblePages[1].assetId] : undefined} side="right" /> : null}
          </div>
        )}
      </div>
      <div className="book-controls">
        <button type="button" className="book-control" onClick={() => move("prev")} disabled={!canGoBack} aria-label="上一页"><CaretLeft size={18} />上一页</button>
        <span className="book-progress" aria-live="polite"><strong>{progress}</strong><small>{isMobile ? "单页阅读" : "双页阅读"}</small></span>
        <button type="button" className="book-control" onClick={() => move("next")} disabled={!canGoForward} aria-label="下一页">下一页<CaretRight size={18} /></button>
      </div>
    </section>
  );
}
